import React        from 'react';
import Button       from '@widesk-ui/components/Button';
import View         from '@widesk-ui/components/View';
import { message }  from '@widesk-ui/hooks/useMessage';

let counter = 0;

/**
 * Confirmation d'une création, avec une annulation à portée de clic.
 *
 * Nos écrans créent l'objet AVANT d'ouvrir sa fiche (« créer puis nommer ») : c'est ce qui permet à
 * la fiche d'enregistrer au fil de l'eau, sans bouton à valider. Mais cela retire la porte de sortie
 * qu'offrait un formulaire à valider — on ne pouvait plus renoncer, seulement supprimer plus tard ce
 * qu'on venait de créer par curiosité.
 *
 * Le message se REMPLACE en place (même `key`) au lieu de s'empiler, et l'annulation ne peut jouer
 * qu'une fois : sans ces deux gardes, le toast restait affiché après coup et chaque nouveau clic
 * repartait supprimer un objet déjà supprimé — donc autant de 404.
 *
 * `onUndo` doit laisser remonter ses erreurs : c'est ici qu'elles sont présentées, et c'est ce qui
 * permet de réoffrir l'annulation quand elle a échoué.
 */
export default function messageWithUndo(text: React.ReactNode, onUndo: () => Promise<void>) {
	const key = `undo-${++counter}`;

	let pending = false;

	const offer = () => message.open({
		key,
		type: 'success',
		duration: 8, // Le temps de lire, de comprendre, et de changer d'avis.
		content: (
			<View row centerV gap="xs" style={{ display: 'inline-flex' }}>
				{text}

				<Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={undo}>
					Annuler
				</Button>
			</View>
		),
	});

	async function undo() {
		if (pending) return;

		pending = true;
		message.open({ key, type: 'loading', duration: 0, content: 'Annulation…' });

		try {
			await onUndo();

			message.open({ key, type: 'success', duration: 1.5, content: 'Annulé.' });
		} catch (err) {
			// L'annulation a échoué : on la repropose plutôt que de laisser l'utilisateur sans recours.
			pending = false;
			message.exception(err);
			offer();
		}
	}

	offer();
}
